import { useState, useEffect, useCallback } from 'react';
import { CadastroParcial, CadastroParcialFormData } from '../types';
import { 
  createCadastroParcial, 
  getCadastrosParciais, 
  getCadastrosParciaisByFuncionario,
  updateCadastroParcial, 
  deleteCadastroParcial 
} from '../services/cadastroParcialService';
import { toast } from 'react-toastify';

export const useCadastrosParciais = (userId: string, codigoFuncionario?: string) => {
  const [cadastros, setCadastros] = useState<CadastroParcial[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  const loadCadastros = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const result = codigoFuncionario 
        ? await getCadastrosParciaisByFuncionario(codigoFuncionario, userId)
        : await getCadastrosParciais(userId);
      
      if (result.success && result.data) {
        setCadastros(result.data);
      } else {
        setError(result.error || 'Erro ao carregar cadastros');
        toast.error(result.error || 'Erro ao carregar cadastros');
      }
    } catch (err) {
      const errorMessage = 'Erro ao carregar cadastros';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [userId, codigoFuncionario]);

  const addCadastro = async (cadastroData: CadastroParcialFormData) => {
    try {
      setError('');
      const result = await createCadastroParcial(cadastroData, userId);
      
      if (result.success && result.data) {
        setCadastros(prev => [result.data!, ...prev]);
        toast.success('Cadastro salvo com sucesso!');
        return { success: true };
      } else {
        const errorMessage = result.error || 'Erro ao salvar cadastro';
        setError(errorMessage);
        toast.error(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (err) {
      const errorMessage = 'Erro ao salvar cadastro';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const updateCadastro = async (id: string, updates: Partial<CadastroParcial>) => {
    try {
      setError('');
      const result = await updateCadastroParcial(id, updates);
      
      if (result.success) {
        setCadastros(prev => 
          prev.map(cadastro => 
            cadastro.id === id ? { ...cadastro, ...updates } : cadastro
          )
        );
        toast.success('Cadastro atualizado com sucesso!');
        return { success: true };
      } else {
        const errorMessage = result.error || 'Erro ao atualizar cadastro';
        setError(errorMessage);
        toast.error(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (err) {
      const errorMessage = 'Erro ao atualizar cadastro';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const removeCadastro = async (id: string) => {
    try {
      setError('');
      const result = await deleteCadastroParcial(id);
      
      if (result.success) {
        setCadastros(prev => prev.filter(cadastro => cadastro.id !== id));
        toast.success('Cadastro removido com sucesso!');
        return { success: true };
      } else {
        const errorMessage = result.error || 'Erro ao remover cadastro';
        setError(errorMessage);
        toast.error(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (err) {
      const errorMessage = 'Erro ao remover cadastro';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  useEffect(() => {
    if (userId) {
      loadCadastros();
    }
  }, [userId, codigoFuncionario, loadCadastros]);

  return {
    cadastros,
    loading,
    error,
    loadCadastros,
    addCadastro,
    updateCadastro,
    removeCadastro
  };
};
